import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Lock, ShieldCheck, Loader2, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { getProduct, ALL_PRODUCTS } from "@/lib/catalog";
import { notifySignup } from "@/lib/api";
import PayPalCheckout from "@/components/PayPalCheckout";
import Countdown from "@/components/Countdown";
import NewReleaseBadge from "@/components/NewReleaseBadge";
import BundleSavingsNote from "@/components/BundleSavingsNote";
import GiftEReaderButton from "@/components/GiftEReaderButton";
import { useBookReviews, RatingSummary, LatestReviews } from "@/components/BookReviews";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = getProduct(id);
  const reviews = useBookReviews(id);
  const [email, setEmail] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notified, setNotified] = useState(false);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-40 text-center">
        <h1 className="font-serif-display text-4xl">Title not found</h1>
        <Link to="/" className="text-primary-gold font-mono-ui text-sm mt-6 inline-block">
          ← Back home
        </Link>
      </div>
    );
  }

  const others = ALL_PRODUCTS.filter((p) => p.id !== product.id).slice(0, 4);

  const typeLabel = { song: "Single", bundle: "Bundle" }[product.type] || "eBook";

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNotifyLoading(true);
    try {
      await notifySignup(email, product.id);
      setNotified(true);
      toast.success("You're on the list! We'll email you when it launches.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  };

  return (
    <div className="pt-28">
      {/* ambient bg */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img src={product.cover} alt="" className="w-full h-full object-cover opacity-10 blur-2xl" />
        <div className="absolute inset-0 bg-[#0B0C10]/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 pb-24">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-28 relative max-w-md mx-auto w-full">
            {product.newRelease && <NewReleaseBadge large />}
            <img
              src={product.cover}
              alt={product.fullTitle || product.title}
              data-testid="product-cover"
              className="w-full max-w-md mx-auto border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.7)] fade-up"
            />
          </div>

          <div className="fade-up" style={{ animationDelay: "0.1s" }}>
            <span
              className="font-mono-ui text-xs uppercase tracking-[0.25em] px-3 py-1 border"
              style={{ color: product.accent, borderColor: `${product.accent}66` }}
            >
              {product.genre}
            </span>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1] mt-6">
              {product.fullTitle || product.title}
            </h1>
            <p className="font-serif-display text-2xl text-primary-gold italic mt-4">
              "{product.hook}"
            </p>
            {product.type === "book" && <RatingSummary reviews={reviews} productId={product.id} />}

            <div className="mt-8 space-y-5 text-soft text-base leading-relaxed">
              {product.narrative ? (
                <>
                  <p>{product.narrative}</p>
                  {product.narrative2 && <p>{product.narrative2}</p>}
                  {product.narrative3 && <p>{product.narrative3}</p>}
                </>
              ) : (
                <>
                  <p>{product.blurb}</p>
                  {product.extra && <p>{product.extra}</p>}
                </>
              )}
            </div>

            {product.setting && (
              <p className="mt-6 text-sm text-soft" data-testid="product-setting">
                <span className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold mr-2">Setting</span>
                {product.setting}
              </p>
            )}

            {product.characters && (
              <div className="mt-8" data-testid="product-characters">
                <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold">Key Characters</h3>
                <ul className="mt-4 space-y-3">
                  {product.characters.map((c) => (
                    <li key={c.name} className="text-soft text-sm leading-relaxed">
                      <span className="font-serif-display text-lg text-text-primary">{c.name}</span> — {c.desc}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 border border-white/10 bg-[#121212]/80 backdrop-blur p-6 sm:p-8">
              {product.available ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.2em] text-soft">
                      {typeLabel}
                    </span>
                    <span className="font-serif-display text-4xl text-primary-gold">
                      ${product.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-6" data-testid="buy-paypal">
                    <PayPalCheckout product={product} />
                  </div>
                  <p className="flex items-center gap-2 justify-center text-soft text-xs mt-4 font-mono-ui">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary-gold" /> Secure PayPal checkout ·
                    Instant download
                  </p>
                  {product.type === "book" && <BundleSavingsNote />}
                  {product.type === "book" && <GiftEReaderButton testId="product-gift-ereader" />}
                </>
              ) : (
                <div className="py-2">
                  <div className="inline-flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.2em] text-soft">
                    <Lock className="w-4 h-4" />
                    {product.releaseDate ? `Available ${product.releaseDate}` : "Coming Soon"}
                  </div>
                  <p className="text-soft text-sm mt-4">
                    {product.releaseDate
                      ? `${product.title} releases ${product.releaseDate} — stay tuned. Leave your email and we'll let you know the moment it drops.`
                      : `${product.title} is currently in the works. Be the first to know when it launches.`}
                  </p>

                  {product.releaseISO && (
                    <div className="mt-6">
                      <Countdown targetISO={product.releaseISO} />
                    </div>
                  )}

                  {notified ? (
                    <div
                      data-testid="notify-success"
                      className="mt-6 flex items-center gap-3 border border-primary-gold/30 bg-primary-gold/5 px-4 py-4"
                    >
                      <Check className="w-5 h-5 text-primary-gold shrink-0" />
                      <p className="text-sm text-text-primary">
                        You're on the list. We'll be in touch on launch day.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleNotify} className="mt-6 space-y-3" data-testid="notify-form">
                      <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-primary-gold transition-colors">
                        <Mail className="w-4 h-4 text-soft shrink-0" />
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          data-testid="notify-email-input"
                          className="w-full bg-transparent py-3 text-text-primary placeholder:text-soft/60 focus:outline-none text-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={notifyLoading}
                        data-testid="notify-submit-button"
                        className="btn-ghost w-full py-3.5 flex items-center justify-center gap-2 font-mono-ui text-xs uppercase tracking-[0.2em]"
                      >
                        {notifyLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Signing up…
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" /> Notify Me at Launch
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
            {product.type === "book" && <LatestReviews reviews={reviews} productId={product.id} />}
          </div>
        </div>

        {/* lyrics */}
        {product.lyrics && (
          <div className="mt-28 max-w-3xl" data-testid="lyrics-section">
            <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#2ec5d6]">
              The Words
            </span>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 mb-10">Lyrics</h2>
            <div className="space-y-8">
              {product.lyrics.map((block, i) => (
                <div key={`${block.section}-${i}`}>
                  <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold mb-3">
                    {block.section}
                  </p>
                  <div className="space-y-1">
                    {block.lines.map((line, j) => (
                      <p key={`${line}-${j}`} className="font-serif-display text-xl sm:text-2xl text-text-primary/90 leading-snug">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* more */}
        <div className="mt-28">
          <h2 className="font-serif-display text-3xl mb-10">More to explore</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {others.map((p) => (
              <Link
                key={p.id}
                to={`/books/${p.id}`}
                data-testid={`related-${p.id}`}
                className="card-hover group block border border-white/10 bg-[#121212]"
              >
                <div className="aspect-[2/3] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-serif-display text-lg leading-tight">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
